// src/utils/quote/quoteValidation.js
// Per-step validators returning { errors }. Keys match draft state field names.
//
// i18n: error VALUES here are i18n key paths (e.g. "wizard.validation.addressRequired"),
// not literal Finnish text — the Step components (StepAddress, StepPump,
// StepConcrete, StepReview/LaskutusSection) call t(errors.field) at render time.
// This module itself needs no `t` param and no React/hook access: RequestForm.jsx
// (out of this task's scope) calls these validators and only ever checks
// Object.keys(...).length, never the message content, so it's unaffected.

import { isRaekokoForbiddenByLine } from "./pumppuKesto";

// Accepts "0", "0.0", "00", " 0 ", numeric 0 — anything that means
// "user explicitly entered zero". Empty/null/non-numeric → false.
export const isZeroValue = (v) =>
    v != null && String(v).trim() !== "" && Number(v) === 0;

// Customer-entered amount is ordered in whole 0.5 m³ increments (1, 1.5, 2, …).
// Tolerance guards float noise (1.5*2 = 3 exactly, 1.25*2 = 2.5 fails). This is a
// Jerry customer-side rule only — operators on betoni.online may adjust to finer
// fractions in rare cases, and that path is intentionally not gated here.
export const isHalfStepM3 = (m3) => Math.abs(m3 * 2 - Math.round(m3 * 2)) < 1e-9;

export function validateAddress(draft) {
    const errors = {};
    if (!draft.osoite?.trim()) errors.osoite = "wizard.validation.addressRequired";
    if (draft.feasibility?.geocoded === false) {
        errors.osoite = "wizard.validation.addressNotFound";
    }
    return errors;
}

export function validateConcrete(draft) {
    const errors = {};
    const m3 = Number(draft.maaraM3);
    if (!draft.maaraM3 || Number.isNaN(m3) || m3 <= 0) {
        errors.maaraM3 = "wizard.validation.amountRequired";
    } else if (m3 < 1) {
        errors.maaraM3 = "wizard.validation.amountMin";
    } else if (!isHalfStepM3(m3)) {
        errors.maaraM3 = "wizard.validation.amountStep";
    }
    if (!draft.kayttokohde) errors.kayttokohde = "wizard.validation.kayttokohdeRequired";
    if (isRaekokoForbiddenByLine(draft.raeKokoId, draft.linjanPituus)) {
        errors.raeKokoId = "wizard.validation.raekokoConflict";
    }
    return errors;
}

export function validatePump(draft) {
    return isZeroValue(draft.puomi) && isZeroValue(draft.linjanPituus)
        ? { puomi: "wizard.validation.pumpBothZero" }
        : {};
}

export function validateDetails() {
    return {};
}

export function validateReview(draft, { isAuthenticated = false, selectedAsiakasId = null } = {}) {
    const errors = {};
    if (!draft.termsAccepted) errors.termsAccepted = "wizard.validation.termsRequired";
    if (isAuthenticated && !selectedAsiakasId) {
        errors.laskutus = "wizard.validation.laskutusRequired";
    }
    return errors;
}

export function validateAll(draft, ctx = {}) {
    return {
        ...validateAddress(draft),
        ...validateConcrete(draft),
        ...validatePump(draft),
        ...validateDetails(draft),
        ...validateReview(draft, ctx),
    };
}

export function validatorForStep(step) {
    return [validateAddress, validatePump, validateConcrete, validateDetails, validateReview][step] || (() => ({}));
}
